import { v1 as uuidv1 } from "uuid";

type RequestParams = {
  url: string;
  method?: "get" | "post" | "put" | "patch" | "delete";
  data?: { [k: string]: unknown };
  params?: object;
};

export enum ResponseStatus {
  OK = 200,
  ERROR = 500,
}

export interface BaseStatusResponse {
  status: number;
  errorMessage: string;
}

export async function requestWithStatus<T extends BaseStatusResponse>({ url, method = "get", params, data }: RequestParams): Promise<T> {
  const timeout = window.WIN?.FetchTimeout || 60000;
  const controller = new AbortController();
  const abortTimeout = setTimeout(() => {
    controller.abort();
  }, timeout);

  const requestInit: RequestInit = {
    method: method.toUpperCase(),
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    signal: controller.signal,
  };

  // cache-busting & url params
  params = params || {};
  params["cacheNoStore"] = uuidv1();
  const token = url.match(/\?./) ? "&" : "?";
  url = url + token + new URLSearchParams(Object.entries(params)).toString();

  // request body
  if (data) {
    requestInit.body = JSON.stringify(data);
  }

  const response = await window.fetch(url, requestInit);
  clearTimeout(abortTimeout);

  if (response.status === 204) {
    // since we can't ready a 204 "no content" response, we only need to return the status code
    const r = { status: response.status } as T;
    return new Promise<T>((resolve) => {
      resolve(r);
    });
  }

  const contentType = response.headers.get("content-type");
  if (contentType && contentType.indexOf("application/json") === -1) {
    const r = { status: response.status } as T;
    return new Promise<T>((resolve) => {
      resolve(r);
    });
  }

  const baseResult = response.json() as Promise<T>;
  const resultWithStatus = baseResult
    .then((r) => {
      r.status = response.status;
      return r;
    })
    .catch((e) => {
      if (process.env.dev) {
        console.log(e);
      }
    });

  if (!response.ok && response.status !== 422) {
    throw new Error(String(response.status || response.statusText));
  }

  return resultWithStatus as Promise<T>;
}

export async function request<T>({ url, method = "get", params, data }: RequestParams): Promise<T> {
  const timeout = window.WIN?.FetchTimeout || 60000;
  const controller = new AbortController();
  const abortTimeout = setTimeout(() => {
    controller.abort();
  }, timeout);

  const requestInit: RequestInit = {
    method: method.toUpperCase(),
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    signal: controller.signal,
  };

  // cache-busting & url params
  params = params || {};
  params["cacheNoStore"] = uuidv1();
  const token = url.match(/\?./) ? "&" : "?";
  url = url + token + new URLSearchParams(Object.entries(params)).toString();

  // request body
  if (data) {
    requestInit.body = JSON.stringify(data);
  }

  const response = await window.fetch(url, requestInit);
  clearTimeout(abortTimeout);

  const result = await (response.json() as Promise<T>).catch((_) => ({}));

  if (!response.ok) {
    throw new Error(String(response.status || response.statusText));
  }

  return result as Promise<T>;
}

export function requestSync<T>({ url, method = "get", params, data }: RequestParams): Promise<T> {
  const requestInit: RequestInit = {
    method: method.toUpperCase(),
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  };
  if (data) {
    requestInit.body = JSON.stringify(data);
  }
  if (params) {
    url = url + "?" + new URLSearchParams(Object.entries(params)).toString();
  }

  return window
    .fetch(url, requestInit)
    .then((response) => response.json())
    .then((data) => {
      return data;
    });
}

export function requestBlob({ url, params }: Partial<RequestParams>): Promise<Blob> {
  const requestInit: RequestInit = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  };

  if (params) {
    url = url + "?" + new URLSearchParams(Object.entries(params)).toString();
  }

  return fetch(url, requestInit).then((response) => {
    if (response.ok) {
      return response.blob();
    }

    return Promise.reject(response);
  });
}
